import { env } from '@/app/config/env';
import { apiClient } from '@/shared/services/api/client';
import { unwrapEnvelope } from '@/shared/services/api/envelope';
import type { ApiEnvelope } from '@/shared/services/api/envelope';
import { RBAC_MOCK_MATRIX, RBAC_MOCK_ROLES } from '../__mocks__/rbac.mock';
import { withDisplayLabels } from '../models/displayLabels';
import { inMastersMenuOrder } from '../models/masterOrder';
import type {
  MatrixSaveResult,
  NewRolePayload,
  PermissionMatrix,
  RbacRole,
} from '../models/rbac.types';

/**
 * The RBAC screen's API.
 *
 * The only place that knows mocks exist, per the repo's single-switch rule. The
 * matrix is read and written whole: the platform replaces a role's grants rather
 * than appending, so clearing a checkbox revokes.
 */

const ROLES = '/roles';

/** A refused screen is the page's news, not grounds to end the session. */
const allowForbidden = { _allowForbidden: true } as const;

const FALLBACK = 'The permissions could not be loaded.';

/** Spring's page envelope, which the role list is wrapped in. */
interface PagedRoles {
  content?: RbacRole[];
}

export const rbacApi = {
  /** Every role, with the permission count the rail shows beside each name. */
  async listRoles(): Promise<RbacRole[]> {
    if (env.ENABLE_MOCKS) return RBAC_MOCK_ROLES;

    const response = await apiClient.get<ApiEnvelope<PagedRoles | RbacRole[]>>(ROLES, {
      ...allowForbidden,
      params: { page: 0, size: 200, sort: 'rank,asc' },
    });

    const data = unwrapEnvelope(response, FALLBACK);
    return Array.isArray(data) ? data : (data.content ?? []);
  },

  /**
   * One role's whole matrix, including the items it grants nothing on.
   *
   * Masters are made the Masters menu on the way in -- its entries, its order (`inMastersMenuOrder`) --
   * and every item is drawn under its menu label (`withDisplayLabels`). Here rather than in the screen,
   * so the mock and the platform arrive in the same shape and the matrix that is saved is the matrix
   * that was read.
   */
  async readMatrix(roleId: number): Promise<PermissionMatrix> {
    if (env.ENABLE_MOCKS) return withDisplayLabels(inMastersMenuOrder(RBAC_MOCK_MATRIX(roleId)));

    return withDisplayLabels(
      inMastersMenuOrder(
        unwrapEnvelope(
          await apiClient.get<ApiEnvelope<PermissionMatrix>>(
            `${ROLES}/${String(roleId)}/permission-matrix`,
            allowForbidden,
          ),
          FALLBACK,
        ),
      ),
    );
  },

  /**
   * Replaces a role's matrix.
   *
   * Sends every item, granted or not, with all five actions: the platform replaces what the role held,
   * so an omitted row would read as a revocation rather than as "unchanged". That includes the rows the
   * screen does not draw (`retained`), which go back exactly as they were read.
   */
  async saveMatrix(roleId: number, matrix: PermissionMatrix): Promise<MatrixSaveResult> {
    const items = [...matrix.groups.flatMap((group) => group.items), ...(matrix.retained ?? [])].map(
      (item) => ({
        featureId: item.featureId,
        create: item.create,
        read: item.read,
        update: item.update,
        delete: item.delete,
        view: item.view,
      }),
    );

    if (env.ENABLE_MOCKS) {
      const granted = items.filter((i) => i.view).length
        + items.filter((i) => i.create).length
        + items.filter((i) => i.read).length
        + items.filter((i) => i.update).length
        + items.filter((i) => i.delete).length;
      return {
        roleId,
        roleName: matrix.roleName,
        granted,
        total: matrix.total,
        ungranted: [],
      };
    }

    return unwrapEnvelope(
      await apiClient.put<ApiEnvelope<MatrixSaveResult>>(
        `${ROLES}/${String(roleId)}/permission-matrix`,
        { items },
      ),
      FALLBACK,
    );
  },

  /** A new role starts with READ on every item and nothing else; the platform seeds that. */
  async createRole(payload: NewRolePayload): Promise<RbacRole> {
    if (env.ENABLE_MOCKS) {
      /* READ on every item and every action on the Dashboard, as the platform seeds a new role. */
      const granted = RBAC_MOCK_MATRIX(-1).granted;
      return { id: Date.now(), name: payload.name, description: payload.description, grantedCount: granted };
    }

    return unwrapEnvelope(
      await apiClient.post<ApiEnvelope<RbacRole>>(ROLES, {
        name: payload.name,
        description: payload.description || null,
        /* The platform's own defaults for a role the screen creates. */
        rank: 100,
        maximumCount: 1000,
        isDefault: false,
      }),
      FALLBACK,
    );
  },

  async updateRole(roleId: number, payload: NewRolePayload): Promise<RbacRole> {
    if (env.ENABLE_MOCKS) {
      return { id: roleId, name: payload.name, description: payload.description };
    }

    return unwrapEnvelope(
      await apiClient.put<ApiEnvelope<RbacRole>>(`${ROLES}/${String(roleId)}`, {
        name: payload.name,
        description: payload.description || null,
        rank: 100,
        maximumCount: 1000,
      }),
      FALLBACK,
    );
  },

  async deleteRole(roleId: number): Promise<void> {
    if (env.ENABLE_MOCKS) return;
    await apiClient.delete<ApiEnvelope<null>>(`${ROLES}/${String(roleId)}`);
  },
};
